'use client';

import { useState, useEffect } from 'react';
import { usePasskey } from '@/hooks/usePasskey';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { X, Fingerprint } from 'lucide-react';

export default function PasskeySetupModal({ show, onClose, onSuccess, onDismiss }) {
  const { registerPasskey, isLoading } = usePasskey();
  const { t } = useTranslation();
  const [deviceName, setDeviceName] = useState('');
  const [error, setError] = useState(null);

  // Pre-fill the device name with an auto-detected default when the modal opens
  // (user can still edit). Runs client-side only → navigator is available.
  useEffect(() => {
    if (show) setDeviceName((prev) => prev || detectDeviceName());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show) return null;

  const handleSetup = async () => {
    setError(null);

    // Auto-detect device name
    const autoDeviceName = deviceName || detectDeviceName();

    try {
      await registerPasskey(autoDeviceName);
      // toast (not window.alert): t() returns a React element → alert() would render
      // it as "[object Object]"; toast accepts a ReactNode and renders it correctly.
      toast.success(t('security.passkeys.setup_modal.success_message'));
      onSuccess();
    } catch (err) {
      // InvalidStateError = this device already has a passkey for this account.
      // That's not a failure — close the prompt and tell the user to just sign in.
      if (err.name === 'InvalidStateError') {
        toast.info(t('security.passkeys.already_registered_on_device', 'Dieses Gerät ist bereits als Passkey für dein Konto registriert — melde dich einfach direkt damit an.'));
        onClose();
        return;
      }
      setError(err.message || t('security.passkeys.errors.setup_failed'));
    }
  };

  const detectDeviceName = () => {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Macintosh|Mac OS/.test(ua)) return 'Mac';
    if (/Windows/.test(ua)) return 'Windows PC';
    if (/Android/.test(ua)) {
      if (/SM-|Samsung/i.test(ua)) return 'Samsung Galaxy';
      if (/Pixel/i.test(ua)) return 'Google Pixel';
      if (/OnePlus/i.test(ua)) return 'OnePlus';
      if (/Xiaomi|Redmi|POCO/i.test(ua)) return 'Xiaomi';
      return 'Android';
    }
    return t('security.passkeys.device_names.my_device');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full p-6 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Fingerprint className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">{t('security.passkeys.setup_modal.title')}</h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t('security.passkeys.setup_modal.description')}
          </p>
        </div>

        {/* Device Name Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t('security.passkeys.setup_modal.device_name_label')}
          </label>
          <Input
            type="text"
            variant="lg"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            placeholder={detectDeviceName()}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={handleSetup}
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? t('security.passkeys.setup_modal.button_setting_up') : t('security.passkeys.setup_modal.button_setup')}
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
          >
            {t('security.passkeys.setup_modal.button_later')}
          </Button>
        </div>

        {/* Nicht wieder anzeigen */}
        {onDismiss && (
          <div className="text-center mt-3">
            <button
              type="button"
              onClick={onDismiss}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors underline"
            >
              {t('security.passkeys.setup_modal.button_dismiss', 'Nicht wieder anzeigen')}
            </button>
          </div>
        )}

        {/* Info Text */}
        <p className="text-xs text-gray-500 text-center mt-4">
          {t('security.passkeys.setup_modal.security_info')}
        </p>
      </div>
    </div>
  );
}
